import React, { useCallback, useState } from "react";
import { useLocation, Link } from "react-router-dom";

import logo from "../../asset/img/onpr_logo_2.png";
import axios from "axios";

const Login = () => {
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState("login"); // "login" or "register"

  // Login form state
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loginErrors, setLoginErrors] = useState({});

  // Registration form state
  const [registrationData, setRegistrationData] = useState({
    firstName: "",
    lastName: "",
    numero_matricule: "",
    email: "",
    password: "",
    password_confirm: "",
    telephone: "",
    description: "",
  });
  const [showRegistrationPassword, setShowRegistrationPassword] =
    useState(false);
  const [showRegistrationPasswordConfirm, setShowRegistrationPasswordConfirm] =
    useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationErrors, setRegistrationErrors] = useState({});
  const [registrationSuccess, setRegistrationSuccess] = useState(null);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear any per-field login errors when user edits email/password
    if (name === "email" || name === "password") {
      setLoginErrors((prev) => {
        if (!prev) return prev;
        if (!prev[name]) return prev;
        return { ...prev, [name]: null };
      });
    }
  };

  const handleRegistrationInputChange = (e) => {
    const { name, value } = e.target;
    setRegistrationData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear any previous success message when user edits fields
    if (registrationSuccess) setRegistrationSuccess(null);
    // Clear error for this field when user starts typing
    if (registrationErrors[name]) {
      setRegistrationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setIsLoading(true);
      try {
        const headers = {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
          "Access-Control-Allow-Origin": "*",
          "Access-Control-Allow-Methods": "GET, POST, PUT, DELETE, OPTIONS",
          "Access-Control-Allow-Headers":
            "Content-Type, Authorization, X-Requested-With",
          "Access-Control-Allow-Credentials": true,
        };

        const response = await axios.post("login", formData, headers);

        console.log(response.data.access_token, response.data.data);
        localStorage.setItem("token", response.data.access_token);
        localStorage.setItem("user", JSON.stringify(response.data.data));
        setIsLoading(false);
        const { from } = location.state || { from: { pathname: "/admin" } };

        // Append authenticated=true to bypass forced logout on destination route
        const separator = from.search
          ? from.search.includes("?")
            ? "&"
            : "?"
          : "?";
        const redirectUrl =
          from.pathname +
          (from.search || "") +
          (from.pathname.includes("client-consultation-cotisation")
            ? separator + "authenticated=true"
            : "");

        window.location.href = redirectUrl;
      } catch (err) {
        setIsLoading(false);
        const msg = err.response?.data?.message;
        if (msg === "Invalid login details") {
          setLoginErrors({
            general: "Identifiants invalides",
          });
        } else if (msg) {
          setLoginErrors({ general: msg });
        } else {
          setLoginErrors({
            general: "Erreur de connexion. Veuillez réessayer.",
          });
        }
      } finally {
        setIsLoading(false);
      }
    },
    [formData],
  );

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleRegistrationSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      // Validation
      const errors = {};
      if (!registrationData.firstName.trim())
        errors.firstName = "Le prénom est requis";
      if (!registrationData.lastName.trim())
        errors.lastName = "Le nom est requis";
      if (!registrationData.numero_matricule.trim())
        errors.numero_matricule = "Le numéro de matricule est requis";
      if (!registrationData.password)
        errors.password = "Le mot de passe est requis";
      if (registrationData.password.length < 6)
        errors.password = "Le mot de passe doit contenir au moins 6 caractères";
      if (registrationData.password !== registrationData.password_confirm) {
        errors.password_confirm = "Les mots de passe ne correspondent pas";
      }

      if (Object.keys(errors).length > 0) {
        setRegistrationErrors(errors);
        return;
      }

      setIsRegistering(true);
      try {
        const headers = {
          "Content-Type": "application/json",
          Accept: "application/json",
        };

        const payload = {
          firstName: registrationData.firstName,
          lastName: registrationData.lastName,
          email: registrationData.email,
          numero_matricule: registrationData.numero_matricule,

          password: registrationData.password,
          telephone: registrationData.telephone || null,
          description: registrationData.description || null,
        };

        const response = await axios.post("saveMember", payload, { headers });

        if (response.data.access_token && response.data.data) {
          localStorage.setItem("token", response.data.access_token);
          localStorage.setItem("user", JSON.stringify(response.data.data));
          setIsRegistering(false);
          const { from } = location.state || {
            from: { pathname: "/declaration-accident" },
          };
          const redirectUrl = from.pathname + (from.search || "");
          window.location.href = redirectUrl;
        } else if (
          response.data?.message === "savemember ok" ||
          response.data?.success ||
          response.status === 200
        ) {
          // Backend saved the member but didn't return tokens — prompt user to login
          setIsRegistering(false);
          setRegistrationSuccess(
            "Inscription réussie. Veuillez vous connecter.",
          );
          setRegistrationErrors({});
          setFormData((prev) => ({ ...prev, email: registrationData.email }));
          setCurrentTab("login");
        }
      } catch (err) {
        setIsRegistering(false);
        if (err.response?.data?.error) {
          // Map server validation arrays to first message strings
          const srv = err.response.data.error;
          const mapped = {};
          Object.keys(srv).forEach((k) => {
            if (Array.isArray(srv[k])) mapped[k] = srv[k][0];
            else mapped[k] = srv[k];
          });
          setRegistrationErrors(mapped);
        } else if (err.response?.data?.message) {
          setRegistrationErrors({ general: err.response.data.message });
        } else {
          setRegistrationErrors({
            general: "Une erreur s'est produite lors de l'inscription",
          });
        }
      }
    },
    [registrationData],
  );

  return (
    <div style={styles.pageWrapper}>
      {/* Header vert ONPR */}
      <div style={styles.greenHeader}>
        OFFICE NATIONAL DES PENSIONS ET RISQUES PROFESSIONNELS DES
        FONCTIONNAIRES, DES MAGISTRATS ET DES AGENTS DE L’ORDRE JUDICIAIRE
      </div>

      {/* Barre rose avec contacts */}
      {/* <div style={styles.pinkBar}>
        <div style={styles.contactInfo}>
          <span style={styles.contactItem}>✉ info@onpr.bi</span>
          <span style={styles.contactItem}>☎ +257 22 24 56 78</span>
          <span style={styles.contactItem}>📍 Bujumbura, Burundi</span>
        </div>
      </div> */}

      {/* Section principale avec logo et formulaire */}
      <div style={styles.mainContent}>
        <div style={styles.logoSection}>
          <img src={logo} alt="Logo ONPR" style={styles.logo} />
          <h1 style={styles.onprTitle}>ONPR</h1>
        </div>

        <div style={styles.container}>
          <div style={styles.card}>
            {/* Tab Navigation */}
            <div style={styles.tabNavigation}>
              <button
                onClick={() => {
                  setCurrentTab("login");
                  setRegistrationErrors({});
                  setRegistrationSuccess(null);
                }}
                style={{
                  ...styles.tabButton,
                  ...(currentTab === "login"
                    ? styles.tabButtonActive
                    : styles.tabButtonInactive),
                }}
              >
                Connexion
              </button>
              <button
                onClick={() => {
                  setCurrentTab("register");
                  setRegistrationErrors({});
                  setRegistrationSuccess(null);
                }}
                style={{
                  ...styles.tabButton,
                  ...(currentTab === "register"
                    ? styles.tabButtonActive
                    : styles.tabButtonInactive),
                }}
              >
                Inscription Membre
              </button>
            </div>

            {/* Login Form */}
            {currentTab === "login" && (
              <>
                {/* <div style={styles.header}>
                  <h2 style={styles.title}>Connexion</h2>
                  <p style={styles.subtitle}>
                    Accédez à votre espace personnel
                  </p>
                </div> */}

                {registrationSuccess && (
                  <div style={styles.successBanner}>{registrationSuccess}</div>
                )}
                {loginErrors.general && (
                  <div style={styles.errorBanner}>{loginErrors.general}</div>
                )}

                <div style={styles.formContainer}>
                  <div style={styles.formGroup}>
                    <label htmlFor="email" style={styles.label}>
                      <svg
                        style={styles.icon}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      Adresse email, Téléphone ou Matricule
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="votre@email.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      style={{
                        ...styles.input,
                        ...(loginErrors.email ? styles.inputError : {}),
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#03a54f";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(45, 95, 63, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = loginErrors.email
                          ? "#d32f2f"
                          : "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                    {loginErrors.email && (
                      <span style={styles.errorText}>{loginErrors.email}</span>
                    )}
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="password" style={styles.label}>
                      <svg
                        style={styles.icon}
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                        />
                      </svg>
                      Mot de passe
                    </label>
                    <div style={styles.passwordWrapper}>
                      <input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        name="password"
                        placeholder="Votre mot de passe"
                        value={formData.password}
                        onChange={handleInputChange}
                        required
                        style={{
                          ...styles.passwordInput,
                          ...(loginErrors.password ? styles.inputError : {}),
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#2d5f3f";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(45, 95, 63, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = loginErrors.password
                            ? "#d32f2f"
                            : "#d1d5db";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                      {loginErrors.password && (
                        <span style={styles.errorText}>
                          {loginErrors.password}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={togglePasswordVisibility}
                        style={styles.toggleButton}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.color = "#2d5f3f")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.color = "#6b7280")
                        }
                      >
                        <svg
                          style={styles.eyeIcon}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          {showPassword ? (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                            />
                          ) : (
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                            />
                          )}
                        </svg>
                      </button>
                    </div>
                    <div style={{ textAlign: "right", marginTop: "8px" }}>
                      <Link
                        to="/forgot-password"
                        style={styles.forgotPasswordLink}
                      >
                        Mot de passe oublié?
                      </Link>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isLoading}
                    style={{
                      ...styles.submitButton,
                      ...(isLoading ? styles.submitButtonDisabled : {}),
                    }}
                    onMouseEnter={(e) => {
                      if (!isLoading) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 20px rgba(45, 95, 63, 0.3)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(45, 95, 63, 0.2)";
                    }}
                  >
                    {isLoading ? (
                      <>
                        <span style={styles.spinner}></span>
                        Connexion en cours...
                      </>
                    ) : (
                      <>
                        <svg
                          style={styles.buttonIcon}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
                          />
                        </svg>
                        Se connecter
                      </>
                    )}
                  </button>
                </div>
              </>
            )}

            {/* Registration Form */}
            {currentTab === "register" && (
              <>
                {/* <div style={styles.header}>
                  <h2 style={styles.title}>Inscription Membre</h2>
                  <p style={styles.subtitle}>Créez votre compte</p>
                </div> */}

                {registrationErrors.general && (
                  <div style={styles.errorBanner}>
                    {registrationErrors.general}
                  </div>
                )}

                <div style={styles.formContainer}>
                  <div style={styles.twoColumnRow}>
                    <div style={styles.formGroup}>
                      <label htmlFor="firstName" style={styles.label}>
                        Prénom
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        placeholder="Votre prénom"
                        value={registrationData.firstName}
                        onChange={handleRegistrationInputChange}
                        required
                        style={{
                          ...styles.input,
                          ...(registrationErrors.firstName
                            ? styles.inputError
                            : {}),
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#03a54f";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(45, 95, 63, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor =
                            registrationErrors.firstName
                              ? "#d32f2f"
                              : "#d1d5db";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                      {registrationErrors.firstName && (
                        <span style={styles.errorText}>
                          {registrationErrors.firstName}
                        </span>
                      )}
                    </div>

                    <div style={styles.formGroup}>
                      <label htmlFor="lastName" style={styles.label}>
                        Nom
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        placeholder="Votre nom"
                        value={registrationData.lastName}
                        onChange={handleRegistrationInputChange}
                        required
                        style={{
                          ...styles.input,
                          ...(registrationErrors.lastName
                            ? styles.inputError
                            : {}),
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#03a54f";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(45, 95, 63, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor =
                            registrationErrors.lastName ? "#d32f2f" : "#d1d5db";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                      {registrationErrors.lastName && (
                        <span style={styles.errorText}>
                          {registrationErrors.lastName}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="numero_matricule" style={styles.label}>
                      Numéro de Matricule *
                    </label>
                    <input
                      type="text"
                      id="numero_matricule"
                      name="numero_matricule"
                      placeholder="Votre numéro de matricule"
                      value={registrationData.numero_matricule}
                      onChange={handleRegistrationInputChange}
                      required
                      style={{
                        ...styles.input,
                        ...(registrationErrors.numero_matricule
                          ? styles.inputError
                          : {}),
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#03a54f";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(45, 95, 63, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor =
                          registrationErrors.numero_matricule
                            ? "#d32f2f"
                            : "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                    {registrationErrors.numero_matricule && (
                      <span style={styles.errorText}>
                        {registrationErrors.numero_matricule}
                      </span>
                    )}
                  </div>

                  <div style={styles.formGroup}>
                    <label htmlFor="email" style={styles.label}>
                      Email *
                    </label>
                    <input
                      type="text"
                      id="email "
                      name="email"
                      placeholder="Votre email"
                      value={registrationData.email}
                      onChange={handleRegistrationInputChange}
                      required
                      style={{
                        ...styles.input,
                        ...(registrationErrors.email ? styles.inputError : {}),
                      }}
                      onFocus={(e) => {
                        e.target.style.borderColor = "#03a54f";
                        e.target.style.boxShadow =
                          "0 0 0 3px rgba(45, 95, 63, 0.1)";
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor =
                          registrationErrors.numero_matricule
                            ? "#d32f2f"
                            : "#d1d5db";
                        e.target.style.boxShadow = "none";
                      }}
                    />
                    {registrationErrors.email && (
                      <span style={styles.errorText}>
                        {registrationErrors.email}
                      </span>
                    )}
                  </div>

                  <div style={styles.twoColumnRow}>
                    <div style={styles.formGroup}>
                      <label htmlFor="reg_password" style={styles.label}>
                        Mot de passe *
                      </label>
                      <div style={styles.passwordWrapper}>
                        <input
                          type={showRegistrationPassword ? "text" : "password"}
                          id="reg_password"
                          name="password"
                          placeholder="Minimum 6 caractères"
                          value={registrationData.password}
                          onChange={handleRegistrationInputChange}
                          required
                          style={{
                            ...styles.passwordInput,
                            ...(registrationErrors.password
                              ? styles.inputError
                              : {}),
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#03a54f";
                            e.target.style.boxShadow =
                              "0 0 0 3px rgba(45, 95, 63, 0.1)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor =
                              registrationErrors.password
                                ? "#d32f2f"
                                : "#d1d5db";
                            e.target.style.boxShadow = "none";
                          }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowRegistrationPassword(
                              !showRegistrationPassword,
                            )
                          }
                          style={styles.toggleButton}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#2d5f3f")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#6b7280")
                          }
                        >
                          <svg
                            style={styles.eyeIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            {showRegistrationPassword ? (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                              />
                            ) : (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            )}
                          </svg>
                        </button>
                      </div>
                      {registrationErrors.password && (
                        <span style={styles.errorText}>
                          {registrationErrors.password}
                        </span>
                      )}
                    </div>

                    <div style={styles.formGroup}>
                      <label htmlFor="password_confirm" style={styles.label}>
                        Confirmer mot de passe *
                      </label>
                      <div style={styles.passwordWrapper}>
                        <input
                          type={
                            showRegistrationPasswordConfirm
                              ? "text"
                              : "password"
                          }
                          id="password_confirm"
                          name="password_confirm"
                          placeholder="Confirmez votre mot de passe"
                          value={registrationData.password_confirm}
                          onChange={handleRegistrationInputChange}
                          required
                          style={{
                            ...styles.passwordInput,
                            ...(registrationErrors.password_confirm
                              ? styles.inputError
                              : {}),
                          }}
                          onFocus={(e) => {
                            e.target.style.borderColor = "#03a54f";
                            e.target.style.boxShadow =
                              "0 0 0 3px rgba(45, 95, 63, 0.1)";
                          }}
                          onBlur={(e) => {
                            e.target.style.borderColor =
                              registrationErrors.password_confirm
                                ? "#d32f2f"
                                : "#d1d5db";
                            e.target.style.boxShadow = "none";
                          }}
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setShowRegistrationPasswordConfirm(
                              !showRegistrationPasswordConfirm,
                            )
                          }
                          style={styles.toggleButton}
                          onMouseEnter={(e) =>
                            (e.currentTarget.style.color = "#2d5f3f")
                          }
                          onMouseLeave={(e) =>
                            (e.currentTarget.style.color = "#6b7280")
                          }
                        >
                          <svg
                            style={styles.eyeIcon}
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                          >
                            {showRegistrationPasswordConfirm ? (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                              />
                            ) : (
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                              />
                            )}
                          </svg>
                        </button>
                      </div>
                      {registrationErrors.password_confirm && (
                        <span style={styles.errorText}>
                          {registrationErrors.password_confirm}
                        </span>
                      )}
                    </div>
                  </div>

                  <div style={styles.twoColumnRow}>
                    <div style={styles.formGroup}>
                      <label htmlFor="telephone" style={styles.label}>
                        Téléphone (Optionnel)
                      </label>
                      <input
                        type="tel"
                        id="telephone"
                        name="telephone"
                        placeholder="Votre numéro de téléphone"
                        value={registrationData.telephone}
                        onChange={handleRegistrationInputChange}
                        style={styles.input}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#03a54f";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(45, 95, 63, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#d1d5db";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                    </div>

                    <div style={styles.formGroup}>
                      <label htmlFor="description" style={styles.label}>
                        Description (Optionnel)
                      </label>
                      <input
                        type="text"
                        id="description"
                        name="description"
                        placeholder="Informations supplémentaires"
                        value={registrationData.description}
                        onChange={handleRegistrationInputChange}
                        style={styles.input}
                        onFocus={(e) => {
                          e.target.style.borderColor = "#03a54f";
                          e.target.style.boxShadow =
                            "0 0 0 3px rgba(45, 95, 63, 0.1)";
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = "#d1d5db";
                          e.target.style.boxShadow = "none";
                        }}
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleRegistrationSubmit}
                    disabled={isRegistering}
                    style={{
                      ...styles.submitButton,
                      ...(isRegistering ? styles.submitButtonDisabled : {}),
                    }}
                    onMouseEnter={(e) => {
                      if (!isRegistering) {
                        e.currentTarget.style.transform = "translateY(-2px)";
                        e.currentTarget.style.boxShadow =
                          "0 6px 20px rgba(45, 95, 63, 0.3)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow =
                        "0 2px 8px rgba(45, 95, 63, 0.2)";
                    }}
                  >
                    {isRegistering ? (
                      <>
                        <span style={styles.spinner}></span>
                        Inscription en cours...
                      </>
                    ) : (
                      <>
                        <svg
                          style={styles.buttonIcon}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                          />
                        </svg>
                        S'inscrire
                      </>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>

          <div style={styles.footer}>
            <svg
              style={styles.shieldIcon}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
            Vos données sont protégées et sécurisées
          </div>
        </div>
      </div>
    </div>
  );
};

const primaryColor = "#03a54f";
const secondaryColor = "#d32f2f";

const styles = {
  pageWrapper: {
    minHeight: "100vh",
    backgroundColor: "#f9fafb",
    fontFamily:
      '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  greenHeader: {
    backgroundColor: primaryColor,
    color: "white",
    textAlign: "center",
    padding: "12px 20px",
    fontSize: "13px",
    fontWeight: "500",
    letterSpacing: "0.3px",
    textTransform: "uppercase",
  },
  pinkBar: {
    backgroundColor: "#fbf9f9ff",
    padding: "10px 20px",
    borderBottom: `1px solid ${secondaryColor}`,
  },
  contactInfo: {
    display: "flex",
    justifyContent: "center",
    gap: "32px",
    flexWrap: "wrap",
    fontSize: "14px",
    color: secondaryColor,
  },
  contactItem: {
    display: "hide",
    alignItems: "center",
    gap: "6px",
    fontSize: "0.9rem",
    fontWeight: "bold",
  },
  mainContent: {
    padding: "40px 20px",
  },
  logoSection: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "16px",
    marginBottom: "40px",
  },
  logo: {
    height: "70px",
    objectFit: "contain",
  },
  onprTitle: {
    fontSize: "42px",
    fontWeight: "700",
    color: primaryColor,
    margin: 0,
  },
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  card: {
    backgroundColor: "white",
    borderRadius: "16px",
    boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
    border: "1px solid #e5e7eb",
    padding: "40px",
    width: "100%",
    maxWidth: "600px",
  },
  tabNavigation: {
    display: "flex",
    gap: "12px",
    marginBottom: "32px",
    borderBottom: "2px solid #e5e7eb",
  },
  tabButton: {
    flex: 1,
    padding: "12px 16px",
    fontSize: "15px",
    fontWeight: "600",
    border: "none",
    background: "none",
    cursor: "pointer",
    transition: "all 0.3s ease",
    borderBottomWidth: "3px",
    borderBottomStyle: "solid",
  },
  tabButtonActive: {
    color: primaryColor,
    borderBottomColor: primaryColor,
  },
  tabButtonInactive: {
    color: "#9ca3af",
    borderBottomColor: "transparent",
  },
  header: {
    textAlign: "center",
    marginBottom: "32px",
    paddingBottom: "20px",
    borderBottom: "2px solid #2d5f3f",
  },
  title: {
    fontSize: "26px",
    fontWeight: "700",
    color: primaryColor,
    margin: "0 0 8px 0",
  },
  subtitle: {
    fontSize: "15px",
    color: "#6b7280",
    margin: 0,
  },
  formContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "24px",
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
  },
  twoColumnRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
  },
  label: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    fontSize: "14px",
    fontWeight: "600",
    color: "#374151",
  },
  icon: {
    width: "18px",
    height: "18px",
    color: primaryColor,
  },
  input: {
    width: "100%",
    padding: "12px 16px",
    fontSize: "15px",
    border: "2px solid #d1d5db",
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  inputError: {
    borderColor: "#d32f2f",
  },
  errorText: {
    fontSize: "12px",
    color: "#d32f2f",
    marginTop: "4px",
  },
  errorBanner: {
    backgroundColor: "#ffebee",
    border: "1px solid #d32f2f",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "20px",
    color: "#c62828",
    fontSize: "14px",
    fontWeight: "500",
  },
  successBanner: {
    backgroundColor: "#ecfdf5",
    border: "1px solid #34d399",
    borderRadius: "8px",
    padding: "12px 16px",
    marginBottom: "20px",
    color: "#065f46",
    fontSize: "14px",
    fontWeight: "500",
  },
  passwordWrapper: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  passwordInput: {
    width: "100%",
    padding: "12px 48px 12px 16px",
    fontSize: "15px",
    border: "2px solid #d1d5db",
    borderRadius: "8px",
    outline: "none",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  toggleButton: {
    position: "absolute",
    right: "12px",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "8px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#6b7280",
    transition: "color 0.2s ease",
  },
  eyeIcon: {
    width: "20px",
    height: "20px",
  },
  submitButton: {
    width: "100%",
    padding: "14px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    backgroundColor: primaryColor,
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 8px rgba(45, 95, 63, 0.2)",
    fontFamily: "inherit",
    marginTop: "8px",
  },
  submitButtonDisabled: {
    opacity: 0.6,
    cursor: "not-allowed",
  },
  buttonIcon: {
    width: "20px",
    height: "20px",
  },
  spinner: {
    width: "20px",
    height: "20px",
    border: "3px solid rgba(255, 255, 255, 0.3)",
    borderTop: "3px solid white",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
    display: "inline-block",
  },
  footer: {
    marginTop: "24px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    fontSize: "13px",
    color: "#6b7280",
  },
  shieldIcon: {
    width: "16px",
    height: "16px",
    color: primaryColor,
  },
  forgotPasswordLink: {
    fontSize: "13px",
    color: "#03a54f",
    textDecoration: "none",
    fontWeight: "600",
    transition: "color 0.2s ease",
  },
};

// Inject keyframes for animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
document.head.appendChild(styleSheet);

export default Login;
